import {ValidationIssue, ValidationResult} from "../../../model/rate/RateConfigValidator";

/**
 * Helpers for routing validator issues from the document-level
 * {@link ValidationResult} to the editor sub-components that own the
 * sub-tree of the document each issue points at.
 *
 * Validator paths are JSON-pointer-ish strings rooted at the document:
 *
 *   /SHORT_RUN/factors/2/groups/0/cells/123|*
 *
 * A sub-component (e.g. a GroupBuilder) knows its OWN prefix
 * (`/SHORT_RUN/factors/2/groups/0`) and uses
 * {@link subtreeIssues} to filter the global lists down to the
 * issues it cares about. Used to:
 *
 *  - Mark collapsed-section headers red when they hide an error;
 *  - Mark individual fields red via `[class.rce-invalid]`;
 *  - Auto-expand a section if it owns an error.
 *
 * The empty {@link EMPTY_RESULT} is shared so child components can
 * default their `@Input()` without an `if (!validation)` guard.
 */
export const EMPTY_RESULT: ValidationResult = {valid: true, errors: [], warnings: []};

export interface SubtreeIssues {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

/**
 * Filter a {@link ValidationResult} to issues whose `path` falls
 * within the given prefix sub-tree. The prefix matches if it equals
 * the issue path exactly or if the next character after the prefix
 * is `/` — so `/SHORT_RUN/factors/2` matches
 * `/SHORT_RUN/factors/2/groups/0/cells/...` but NOT
 * `/SHORT_RUN/factors/20/...`.
 */
export function subtreeIssues(result: ValidationResult | null | undefined, prefix: string): SubtreeIssues {
  if (!result || !prefix) { return {errors: [], warnings: []}; }
  return {
    errors: result.errors.filter(e => pathMatches(e.path, prefix)),
    warnings: result.warnings.filter(w => pathMatches(w.path, prefix))
  };
}

/** True when an issue at `issuePath` belongs in the `prefix` sub-tree. */
export function pathMatches(issuePath: string, prefix: string): boolean {
  if (!issuePath || !prefix) { return false; }
  if (issuePath === prefix) { return true; }
  if (issuePath.length <= prefix.length) { return false; }
  if (issuePath.indexOf(prefix) !== 0) { return false; }
  return issuePath.charAt(prefix.length) === '/';
}

/** True when any issue exactly targets `path` (not a descendant). */
export function exactErrorAt(result: ValidationResult | null | undefined, path: string): boolean {
  if (!result || !path) { return false; }
  for (const e of result.errors) { if (e.path === path) { return true; } }
  return false;
}

